import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@assets/img/icons/chevron.svg?react';
import { motion, MotionConfig } from 'framer-motion';

import { wheelImages } from '@/assets/img/contract';
import { diceImages } from '@/assets/img/dice-game/dice';
import { usdImages } from '@/assets/img/wallet';
import { AppRoute } from '@/common/app/routes';
import { transfromRewardsToCommonFormat } from '@/common/lib/common';
import { getText } from '@/common/lib/text';
import { type LevelUpSocketEventType } from '@/common/types/socket';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H1, TextSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';

import LevelUpCharacter from './LevelUpCharacter';
import { LevelUpRewardItem } from './LevelUpRewardItem';

import partnersPng from '/img/icons/partners.png';
import partnersWebp from '/img/icons/partners.webp';
import shopPng from '/img/navigation/shop.png';
import shopWebp from '/img/navigation/shop.webp';

type BadgePicture = { png: string; webp: string; alt: string };

type LevelUpAvailableBadgeProps = {
  icon: BadgePicture;
  overlayIcon?: BadgePicture;
  label: string;
  animate: { y: number; opacity: number };
};

type LevelUpBadgeConfig = {
  icon: BadgePicture;
  overlayIcon?: BadgePicture;
  label: string;
};

type LevelUpCtaConfig = {
  buttonText: string;
  badge?: LevelUpBadgeConfig;
  onClose?: () => void;
};

const LevelUpAvailableBadge: FC<LevelUpAvailableBadgeProps> = ({
  icon,
  overlayIcon,
  label,
  animate,
}) => (
  <motion.div
    animate={animate}
    className="flex items-center bg-surface rounded-lg h-10 shrink-0 w-full mt-auto mb-5 pl-2 pr-4"
  >
    <div className="relative size-8 mr-2">
      <Picture img={icon.png} webp={icon.webp} alt={icon.alt} />
      {overlayIcon && (
        <Picture
          className="absolute bottom-0 right-0 w-4"
          img={overlayIcon.png}
          webp={overlayIcon.webp}
          alt={overlayIcon.alt}
        />
      )}
    </div>

    <TextSmall weight="medium" className="mr-auto">
      {label}
    </TextSmall>
    <CheckIcon className="text-accent size-6 shrink-0" />
  </motion.div>
);

const LevelUpClaimRewardScreen: FC<
  LevelUpSocketEventType & {
    onClaim: () => void;
    isClaimed?: boolean;
    isLoading?: boolean;
  }
> = ({
  wallet,
  materials,
  lottery,
  upgrade,
  onClaim,
  isClaimed,
  isLoading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const MotionH1 = motion.create(H1);
  const { closeModal } = useContext(ModalContext);
  const MotionMainButton = motion.create(MainButton);

  const levelCtaMap: Record<number, LevelUpCtaConfig> = {
    2: {
      buttonText: t('Claim and go to Daily goods'),
      badge: {
        icon: {
          png: wheelImages.green.png,
          webp: wheelImages.green.webp,
          alt: 'green wheel',
        },
        overlayIcon: {
          png: usdImages.icon.png,
          webp: usdImages.icon.webp,
          alt: 'usd',
        },
        label: t('"Daily goods" are available now'),
      },
    },
    3: {
      buttonText: t('Claim and go to Roll Craft'),
      badge: {
        icon: {
          png: diceImages.icon.png,
          webp: diceImages.icon.webp,
          alt: 'dice',
        },
        label: t('Roll Craft is available now'),
      },
      onClose: () => navigate(AppRoute.DICE_GAME),
    },
    4: {
      buttonText: t('Claim and go to Shop'),
      badge: {
        icon: { png: shopPng, webp: shopWebp, alt: 'shop' },
        label: t('Shop is available now'),
      },
      onClose: () => navigate(AppRoute.SHOP),
    },
    5: {
      buttonText: t('Claim and go to Partners'),
      badge: {
        icon: { png: partnersPng, webp: partnersWebp, alt: 'partners' },
        label: t('Partners is available now'),
      },
      onClose: () => navigate(AppRoute.PARTNERS),
    },
  };

  const cta: LevelUpCtaConfig = levelCtaMap[upgrade] ?? {
    buttonText: t('claim'),
  };

  const handleClose = (): void => {
    closeModal();
    cta.onClose?.();
  };

  const allRewards = transfromRewardsToCommonFormat([
    wallet,
    materials,
    lottery,
  ]);

  const getLabel = (itemName: string): string => {
    switch (itemName) {
      case 'tgem': {
        return 'T-Gem';
      }
      case 'capitalizationLg': {
        return 'Capitalization';
      }
      case 'silverTicket':
      case 'goldTicket':
      case 'diamondTicket': {
        return 'Ticket';
      }
      default: {
        return getText({ name: itemName, kind: 'resource', t }) || '';
      }
    }
  };

  const slideDown = isClaimed ? { y: 400, opacity: 0 } : { y: 0, opacity: 1 };
  const slideUp = isClaimed ? { y: -400, opacity: 0 } : { y: 0, opacity: 1 };

  return (
    <>
      <MotionConfig transition={{ duration: 0.3 }}>
        <motion.div className="flex flex-col items-center h-full self-stretch">
          <MotionH1 weight="extrabold" className="text-2xl" animate={slideUp}>
            {t('Level Up!')}
          </MotionH1>
          <div className="flex flex-col items-center">
            <LevelUpCharacter upgrade={upgrade} isClaimed={isClaimed} />
            <motion.div
              className="flex w-[20rem] items-center justify-center flex-wrap isolate mb-4"
              animate={slideDown}
            >
              {allRewards.map(({ item, value }) => (
                <LevelUpRewardItem
                  itemName={item}
                  amount={value}
                  label={getLabel(item)}
                />
              ))}
            </motion.div>
          </div>
          {cta.badge && (
            <LevelUpAvailableBadge animate={slideDown} {...cta.badge} />
          )}
          <MotionMainButton
            className="shrink-0"
            text={cta.buttonText}
            onClick={onClaim}
            disabled={isLoading}
            isLoading={isLoading}
            animate={slideDown}
          />
        </motion.div>
      </MotionConfig>
      {isClaimed && (
        <Confetti
          className="inset-0 absolute"
          loop={false}
          onComplete={handleClose}
          speed={1.5}
        />
      )}
    </>
  );
};

export default LevelUpClaimRewardScreen;
